(() => {
  const selector = 'details.n9-lang__details';

  const isLocaleRoot = (pathname) => {
    const path = pathname.replace(/\/+$/, '') || '/';
    return path === '/' || /^\/[a-z]{2}(?:-[a-z]{2})?$/i.test(path);
  };

  // Option and metafield filter values are translated, so they match nothing in another language.
  const translatedFilter = /^filter\.(?:v\.option|v\.m|p\.m)\./;
  const portableSearch = (search) => {
    if (!search) return '';
    const params = new URLSearchParams(search);
    const drop = [];
    params.forEach((value, key) => {
      if (translatedFilter.test(key)) drop.push(key);
    });
    if (!drop.length) return search;
    drop.forEach((key) => params.delete(key));
    const rest = params.toString();
    return rest ? '?' + rest : '';
  };

  const applyLanguageHrefs = () => {
    const current = new URL(window.location.href);
    const hereIsHome = isLocaleRoot(current.pathname);

    document.querySelectorAll('a.n9-lang__option').forEach((link) => {
      const code = link.getAttribute('hreflang') || link.getAttribute('data-value');
      if (!code) return;
      const search = link.getAttribute('aria-current') === 'true' ? current.search : portableSearch(current.search);

      const alt = document.querySelector('link[rel="alternate"][hreflang="' + code + '"]')
        || document.querySelector('link[rel="alternate"][hreflang="' + code.toLowerCase() + '"]')
        || document.querySelector('link[rel="alternate"][hreflang="' + code.split('-')[0].toLowerCase() + '"]');

      let target;
      try {
        target = new URL(alt && alt.href ? alt.href : link.href, window.location.origin);
      } catch (error) {
        return;
      }
      if (target.origin !== window.location.origin) return;
      if (alt && !hereIsHome && isLocaleRoot(target.pathname)) return;

      target.search = search;
      target.hash = current.hash;
      link.href = target.pathname + target.search + target.hash;
    });
  };

  const closeAll = (except) => {
    document.querySelectorAll(selector + '[open]').forEach((node) => {
      if (node !== except) node.removeAttribute('open');
    });
  };

  // On mobile the drawer and footer lists open off-screen or clipped at the bottom of the page.
  // There they move into a bottom sheet and move back when the details element closes.
  const sheetQuery = window.matchMedia('(max-width: 1000px)');
  const sheetScope = '.n9-mobile-meta, .shopify-section-footer';
  let sheet = null;
  let sheetOwner = null;

  const buildSheet = () => {
    const host = document.getElementById('root') || document.body;
    const node = document.createElement('div');
    node.className = 'n9-lang-sheet';
    node.hidden = true;
    node.innerHTML =
      '<div class="n9-lang-sheet__backdrop" data-n9-lang-sheet-close></div>' +
      '<div class="n9-lang-sheet__panel" role="dialog" aria-modal="true">' +
      '<div class="n9-lang-sheet__head">' +
      '<p class="n9-lang-sheet__title"></p>' +
      '<button type="button" class="n9-lang-sheet__close" data-n9-lang-sheet-close></button>' +
      '</div>' +
      '<div class="n9-lang-sheet__body"></div>' +
      '</div>';
    host.appendChild(node);
    node.addEventListener('click', (event) => {
      if (event.target.closest('[data-n9-lang-sheet-close]') && sheetOwner) {
        sheetOwner.removeAttribute('open');
      }
    });
    return node;
  };

  // Shopify's consent banner is fixed above the whole theme and covers the lower half of a
  // phone screen. While it shows, the drawer and the sheet leave that much room at the bottom.
  const consentBanner = () => document.getElementById('shopify-pc__banner');
  const syncConsentOffset = () => {
    const banner = consentBanner();
    let offset = 0;
    if (banner) {
      const style = getComputedStyle(banner);
      const rect = banner.getBoundingClientRect();
      if (style.display !== 'none' && style.visibility !== 'hidden' && rect.height > 0) {
        offset = Math.max(0, Math.round(window.innerHeight - rect.top));
      }
    }
    const root = document.documentElement;
    root.style.setProperty('--n9-consent-offset', offset + 'px');
    root.classList.toggle('n9-consent-open', offset > 0);
  };

  const bannerObserver = new MutationObserver(syncConsentOffset);
  const bannerResize = 'ResizeObserver' in window ? new ResizeObserver(syncConsentOffset) : null;
  let watchedBanner = null;
  const watchBanner = () => {
    const banner = consentBanner();
    if (banner !== watchedBanner) {
      bannerObserver.disconnect();
      if (bannerResize) bannerResize.disconnect();
      watchedBanner = banner;
      if (banner) {
        bannerObserver.observe(banner, { attributes: true, attributeFilter: ['style', 'class', 'hidden', 'aria-hidden'] });
        if (bannerResize) bannerResize.observe(banner);
        banner.addEventListener('transitionend', syncConsentOffset);
        banner.addEventListener('animationend', syncConsentOffset);
      }
    }
    syncConsentOffset();
  };
  new MutationObserver(watchBanner).observe(document.body, { childList: true });
  document.addEventListener('visitorConsentCollected', () => window.setTimeout(syncConsentOffset, 50));
  document.addEventListener('click', syncConsentOffset, true);
  window.addEventListener('resize', syncConsentOffset);
  watchBanner();

  const openSheet = (details) => {
    if (sheetOwner === details) return;
    const form = details.querySelector('.n9-lang__form');
    if (!form) return;
    syncConsentOffset();
    if (!sheet) sheet = buildSheet();
    const item = details.closest('.n9-lang');
    const title = (item && item.getAttribute('data-title')) || '';
    const closeLabel = (item && item.getAttribute('data-close-label')) || '';
    const titleNode = sheet.querySelector('.n9-lang-sheet__title');
    const panel = sheet.querySelector('.n9-lang-sheet__panel');
    titleNode.textContent = title;
    titleNode.id = 'n9-lang-sheet-title';
    panel.setAttribute('aria-labelledby', titleNode.id);
    sheet.querySelector('.n9-lang-sheet__close').setAttribute('aria-label', closeLabel);
    sheet.querySelector('.n9-lang-sheet__body').appendChild(form);
    sheetOwner = details;
    sheet.hidden = false;
    document.documentElement.classList.add('n9-lang-sheet-open');
    requestAnimationFrame(() => {
      sheet.classList.add('is-open');
      const currentOption = form.querySelector('.n9-lang__option[aria-current="true"]') || form.querySelector('.n9-lang__option');
      if (currentOption) currentOption.focus({ preventScroll: true });
    });
  };

  const closeSheet = (details) => {
    if (!sheet || sheetOwner !== details) return;
    const form = sheet.querySelector('.n9-lang__form');
    if (form) details.appendChild(form);
    sheet.classList.remove('is-open');
    sheet.hidden = true;
    sheetOwner = null;
    document.documentElement.classList.remove('n9-lang-sheet-open');
    const summary = details.querySelector('.n9-lang__summary');
    if (summary) summary.focus({ preventScroll: true });
  };

  document.addEventListener('toggle', (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.matches(selector)) return;
    if (!details.closest(sheetScope)) return;
    if (details.open && sheetQuery.matches) {
      openSheet(details);
    } else if (!details.open) {
      closeSheet(details);
    }
  }, true);

  // The toggle event fires a few frames after the tap. Opening the sheet in the tap itself
  // keeps the drawer from painting the inline list first.
  document.addEventListener('click', (event) => {
    const summary = event.target.closest && event.target.closest('summary.n9-lang__summary');
    if (!summary || !sheetQuery.matches) return;
    const details = summary.parentElement;
    if (!(details instanceof HTMLDetailsElement) || !details.matches(selector) || !details.closest(sheetScope)) return;
    event.preventDefault();
    if (details.open) {
      details.removeAttribute('open');
      return;
    }
    details.setAttribute('open', '');
    openSheet(details);
  }, true);

  sheetQuery.addEventListener('change', () => {
    if (!sheetQuery.matches && sheetOwner) sheetOwner.removeAttribute('open');
  });

  document.addEventListener('click', (event) => {
    if (sheet && sheet.contains(event.target)) return;
    const current = event.target.closest(selector);
    closeAll(current);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeAll(null);
  });

  document.documentElement.classList.add('n9-lang-sheet-ready');

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyLanguageHrefs);
  } else {
    applyLanguageHrefs();
  }
  window.addEventListener('localizationForm', applyLanguageHrefs);
})();
